/**
 * `AW48` · **La pantalla de configuración del chatbot.** `R1-F2`.
 *
 * Reemplaza el contenido de la maqueta inerte que estaba en este lugar del menú
 * y **la renombra**: la pantalla dejó de contener sólo mensajes el día que pasó a
 * contener personalidad y reglas de contención. `R1-F2 §3` y el caso `R1-F2-21`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS CINCO PESTAÑAS, Y LAS CUATRO SECCIONES QUE AGRUPAN (`§4`, R1.1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. **Personalidad** — el perfil, en markdown y con vista previa.
 *   2. **Guardrails** — la lista de categorías (`guardrails.tsx`).
 *   3. **Mensajes del motor** — los SEIS.
 *   4. **Derivación** — los DOS.
 *   5. **Auditoría** — la bitácora de cambios (`auditoria.tsx`), que en R1 es una
 *      maqueta: la tabla vacía es el estado real, no un cartel de espera.
 *
 * ⭐ **Cada pestaña tiene dirección propia** (`F3-CA-13` … `F3-CA-18`, PRD:521-526)
 * y las cinco están MONTADAS a la vez: la inactiva se apaga con `hidden`, no se
 * desmonta. Eso es lo que permite que el renglón de guardado viva afuera de los
 * paneles y siga nombrando la sección que guarda. Ver `rutas.ts`, `pestanas.tsx`.
 *
 * ⭐⭐ **OCHO textos llegan al participante; NUEVE campos son obligatorios.** El
 * noveno es el perfil, que **nunca se envía**: se le manda al modelo como prompt
 * del sistema. Los dos números son distintos y confundirlos es el error caro de
 * esta pantalla — ver `reglas.ts`.
 *
 * ⛔ **Y el noveno MENSAJE no existe.** El «no-handoff» que la maqueta listaba y
 * el «Mensaje de no-derivación» de `§4 Sección 4` **son el mismo campo escrito
 * distinto**; contarlos como dos da nueve mensajes, y el noveno no está en
 * ninguna fuente.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ LO QUE NO ENTRA, Y NO SE DIBUJA DESHABILITADO (`§3`)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El mensaje de saludo inicial, el de cierre, los pares de botón y respuesta y
 * **el enlace a la plataforma de tutores** son R2. `§3` cierra la puerta a la
 * salida cómoda con todas las letras: *«Las secciones de R2 no se muestran
 * deshabilitadas en R1. Simplemente no existen todavía. Mostrar controles
 * apagados con una nota es exactamente lo que hace la maqueta actual, y es lo que
 * esta funcionalidad viene a reemplazar.»*
 *
 * ⇒ acá no hay un solo control apagado de forma permanente, y no hay ninguna nota
 * que diga que algo no está construida. El único `disabled` que existe es el que
 * dura lo que dura un pedido en vuelo.
 *
 * ⛔ **Y ningún parámetro del motor entra acá**, por más que cambie el
 * comportamiento del chatbot: `§2.1` lo anticipa palabra por palabra —*«lo que
 * decide el lado no es el efecto, es la naturaleza del valor»*—. No hay modo de
 * apego, no hay `top_k`, no hay piso de relevancia.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ GUARDAR PUBLICA, Y ESO TIENE UNA CONSECUENCIA QUE HAY QUE OPERAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `§5.2`: no hay estado intermedio ni circuito de revisión — quien guarda, deja
 * el texto vigente. Y `§5.8` agrega lo que eso significa: el perfil de
 * personalidad *«es casi todo el prompt del sistema»*, así que quien lo edita
 * *«puede reescribir el comportamiento del chatbot entero»*. **No se construye
 * ninguna salvaguarda en el producto** —limitar el campo sería inútil— y la
 * contención vive en el track de QA: correr el subconjunto sensible del conjunto
 * de evaluación **después** de cada cambio. La pantalla lo dice donde se lee.
 */

import { useCallback, useEffect, useState, type JSX, type ReactNode } from "react"
import type {
  CampoDeTextoDelChatbot,
  CategoriaDeGuardrail,
  TextosDelChatbot,
  TextosOpcionalesDelChatbot,
} from "@contracts/admin-api/index.ts"
import { useBordeConfiguracion } from "../contexto-borde-configuracion.tsx"
import { aLosBrazos, brazos, llegadaDeLaDerivacion, nombresDeLosBrazos } from "../zonas.ts"
import { SeccionDeAuditoria } from "./auditoria.tsx"
import { Contador, ContadorSinTope } from "./contador.tsx"
import { SeccionDeGuardrails } from "./guardrails.tsx"
import { VistaPreviaDelPerfil } from "./markdown.tsx"
import { Panel, Pestanas, usarElHash } from "./pestanas.tsx"
import {
  NOMBRE_DEL_GUARDADO,
  SLUG_DE_LA_ENCUESTA,
  mensajeAbiertoDelHash,
  panelDeCategoriaDelHash,
  pestanaDelHash,
  urlDeLaPestana,
} from "./rutas.ts"
import type { MensajeConDireccion } from "./rutas.ts"
import { TOPE_AL_PARTICIPANTE } from "./borde-configuracion.ts"
import {
  encuestaPasadaDeTope,
  largo,
  puedeGuardarEsteMensaje,
  puedeGuardarLaConfiguracion,
  textosPasadosDeTope,
  textosVacios,
} from "./reglas.ts"

/**
 * Un campo de texto: su nombre en el contrato, su rótulo, cuándo lo recibe el
 * participante y **el slug de su dirección propia**.
 *
 * ⛔ El slug está en la tupla y no derivado del rótulo a propósito: `CA-72` fija
 * `fuera-de-tema`, `CA-74` fija `dame-un-segundo` y `CA-75` fija
 * `no-puedo-responder-eso`, y un slug calculado a partir de un texto con acentos
 * —«No puedo responder eso» → «no-puedo-responder-eso»— se rompe en silencio el
 * día que alguien corrija una tilde. La dirección es contrato: se escribe.
 */
type Campo = readonly [
  /**
   * ⭐⭐ `R3-UI2` · **`CampoDeTextoDelChatbot` y no `keyof TextosDelChatbot`.**
   * La octava fila —«Encuesta de satisfacción»— es
   * `keyof TextosOpcionalesDelChatbot`, un tipo APARTE (`R3-CAA1` · B15), y ése
   * es el punto entero: si entrara en `TextosDelChatbot` heredaría la
   * obligatoriedad de `reglas.ts` y `CA-11` saldría rojo. El contrato ya publica
   * la unión de las dos claves y **se deriva con `keyof`**, así que un texto
   * nuevo —obligatorio u opcional— entra solo.
   */
  campo: CampoDeTextoDelChatbot,
  rotulo: string,
  cuando: string,
  /*
   * ⛔ `MensajeConDireccion` y no `string`: el marco (`src/rutas.ts`) necesita la
   * misma lista para cumplir `CA-84`, y vive en `./rutas.ts`. Tipar acá con el
   * union hace que un slug escrito sólo de este lado **no compile**, en vez de
   * dejar una fila con dirección propia que el marco redirige a la lista.
   */
  slug: MensajeConDireccion,
]

/**
 * Un campo de **derivación**: los mismos cuatro datos, más la tarjeta que lo
 * agrupa y el brazo al que llega.
 *
 * ⭐ `CA-78` y `CA-79` (PRD:587-588) piden que cada tarjeta diga **a quién le
 * llega** su texto. No es una etiqueta decorativa: los dos textos se guardan una
 * sola vez —son de la instancia— y el producto elige uno según el brazo, así que
 * «cuál de los dos le llega a quién» es lo único que el operador no puede deducir
 * de los campos.
 */
type CampoDeDerivacion = readonly [
  campo: keyof TextosDelChatbot,
  rotulo: string,
  cuando: string,
  tarjeta: string,
  /*
   * ⟦rastro · ADR-0082 · 30/9/2026 · fix/consola-literales-de-brazo⟧ Era
   * ~~`llegada: string`~~ con «Llega a: Brazo Puro» / «Llega a: Brazo Híbrido»
   * escritos acá, y sobre `OLI` (un brazo) la tarjeta «sin tutores» anunciaba un
   * brazo que la instancia no declara. Ahora la tupla guarda la CAPACIDAD y el
   * texto lo arma `llegadaDeLaDerivacion()` (`zonas.ts`) con los brazos declarados.
   */
  llegaAlQueDeriva: boolean,
]

/**
 * **Sección 3 · Los SIETE mensajes del motor.** `§4 Sección 3`.
 *
 * ⭐⭐ **RASTRO · `R2-UI9` · 23/9/2026 — ERAN SEIS Y HOY SON SIETE.** `ADR-0082`:
 * el párrafo original de abajo se conserva entero porque su argumento sigue
 * valiendo para los seis que ya estaban, y el único número que cambió es el
 * primero. `PRD_R2.md:1194–1203` suma la **séptima y última** fila: «Botón sin
 * respuesta», *«que es lo que KAI contesta cuando alguien aprieta un botón que no
 * tiene respuesta configurada en su brazo […] Es obligatorio, se edita, no se
 * elimina y es igual para los dos brazos.»* (`R2-F3-CA-65`, `PRD_R2.md:1339`).
 *
 * ⛔ **Entra como una TUPLA MÁS de esta lista y no como un panel propio.** Es la
 * séptima fila de la misma tabla: el editor, el contador, «Obligatorio», el
 * `disabled` de «Guardar mensaje» y la ausencia de «Eliminar» le llegan por
 * construcción. Un componente aparte «porque es de F3» duplicaría el panel y el
 * día que alguien cambie la validación de los otros seis, el séptimo quedaría
 * distinto sin que nada se ponga rojo.
 *
 * ⛔ **Los SEIS textos de abajo NO SE TOCAN** (`R2-F3-CA-70`, `PRD_R2.md:1344`):
 * *«Los mensajes que ya existían no se tocan»*, y tampoco aparece ninguna fila
 * «Bienvenida» ni «Servicio no disponible» — `PRD_R2.md:1190` y `:1192` las dejan
 * afuera **con decisión tomada**, no diferidas.
 *
 * ⭐ **El cuarto elemento de cada tupla es el slug de su dirección propia**, y
 * `CA-72`, `CA-74` y `CA-75` (PRD:580, 582-583) fijan TRES de los seis:
 * `fuera-de-tema`, `dame-un-segundo` y `no-puedo-responder-eso`.
 *
 * ⚠️ **Los otros tres no los fija ningún criterio** —`CA-69` (PRD:577) nombra los
 * seis rótulos pero no les da dirección—, así que acá se escriben con la misma
 * regla que los fijados: el rótulo en minúsculas, sin acentos y con guiones. **Es
 * un hallazgo del PRD, no una decisión de esta pantalla:** si alguien fija
 * `sin-cobertura-en-el-banco` de otra forma, se corrige acá y en ningún otro lado.
 * Sin dirección propia la fila igual se lee —`CA-84` deja la lista—, así que el
 * invento no puede romper nada: sólo no abriría la fila que se pidió abrir.
 *
 * ⛔ Los dos primeros **no son el mismo**, y el PRD le dedica un párrafo:
 * «No puedo responder eso» es para **datos duros** —una carrera, una beca, una
 * fecha—, donde inventar tiene consecuencia directa sobre una decisión del
 * estudiante; «Sin cobertura en el banco» es para todo lo demás que el banco no
 * cubre, y la conversación sigue. Escribirlos con el mismo texto desperdicia la
 * distinción.
 */
const MENSAJES_DEL_MOTOR: readonly Campo[] = [
  [
    "offTopicMessage",
    "Fuera de tema",
    "Pregunta algo fuera del dominio del chatbot.",
    "fuera-de-tema",
  ],
  [
    "mensajeSinDatoDuro",
    "No puedo responder eso",
    "Le piden un dato duro —una carrera, una beca, una fecha— que el chatbot no tiene y no debe inventar.",
    "no-puedo-responder-eso",
  ],
  [
    "mensajeSinCoberturaEnElBanco",
    "Sin cobertura en el banco",
    "El banco no tiene material para lo que se preguntó y el chatbot no pudo armar una respuesta sin datos. La conversación sigue.",
    "sin-cobertura-en-el-banco",
  ],
  [
    "fallbackText",
    "Respuesta de respaldo",
    "El modelo falla o se pasa del tiempo del turno.",
    "respuesta-de-respaldo",
  ],
  [
    "avisoDeDemora",
    "Dame un segundo",
    "La respuesta está tardando más de lo esperado.",
    "dame-un-segundo",
  ],
  [
    "pedidoDeMensajeDeTexto",
    "Pedir que escriba",
    "Mandó un audio, una imagen o un sticker.",
    "pedir-que-escriba",
  ],
  /*
   * ⭐⭐ `R2-UI9` · **LA SÉPTIMA Y ÚLTIMA.** `PRD_R2.md:1196–1201`, textual: el
   * rótulo es «Botón sin respuesta» y el «Cuándo se envía» es *«Apretó un botón
   * que no tiene respuesta automática en su brazo.»* — las dos cadenas se copian
   * carácter por carácter porque `CA-65` las cita.
   *
   * ⚠️ **Va ÚLTIMA y no alfabética**: `CA-65` dice *«la séptima y última fila»*.
   * El orden de esta lista **es** el orden de la tabla.
   */
  [
    "botonSinRespuesta",
    "Botón sin respuesta",
    "Apretó un botón que no tiene respuesta automática en su brazo.",
    "boton-sin-respuesta",
  ],
]

/**
 * ⭐⭐ `R3-UI2` · **La OCTAVA fila, y la única OPCIONAL.** `PRD_R3.md:1749-1757`,
 * `R3-F4-CA-01`.
 *
 * ⛔ **Vive en una lista aparte y no adentro de `MENSAJES_DEL_MOTOR`**, y el
 * motivo es de TIPOS, no de prolijidad: `MENSAJES_DEL_MOTOR` es la lista de los
 * SIETE textos obligatorios que `reglas.ts` recorre —`textosVacios` sale de
 * `TODOS_LOS_TEXTOS`, no de acá, pero el docblock de esta sección dice «los
 * siete» y el PRD también—, y la encuesta **no es uno de ellos**: su valor no
 * vive en `TextosDelChatbot` sino en `TextosOpcionalesDelChatbot`
 * (`R3-CAA1` · B15). Juntarlas en una sola lista obligaría a que el tipo de la
 * tupla mintiera sobre de dónde sale el valor de cada fila.
 *
 * ⭐ **Pero la TABLA es una sola**, y por eso existe `MENSAJES_DE_LA_TABLA` abajo:
 * `PRD_R3.md:1739` pide *«ocho filas fijas, siempre en el mismo orden»*, y dos
 * `.map()` hermanos dejarían la octava fuera del contenedor `__lista` —el que
 * `sistema-visual.md` §3.7 pide con radio y borde— sin que nada se ponga rojo.
 *
 * ⚠️ **El «Cuándo se envía» se copia carácter por carácter de `CA-01`**
 * (`PRD_R3.md:1757`), guillemets incluidos: el criterio lo cita textual y es lo
 * único de esta fila que una persona puede cotejar contra el PRD sin abrir el
 * código. La última oración —*«Es opcional: si está vacío, no se manda.»*— es la
 * que le dice al Administrador por qué esta fila puede quedar en «—».
 */
const LA_ENCUESTA: Campo = [
  "encuestaDeSatisfaccion",
  "Encuesta de satisfacción",
  "Pasó una hora sin mensajes en la conversación. Le llega a quien tiene «Sin interacción: No» y «Opt-out: No», tenga o no «Siguió con el chatbot» o «Pidió humano». Una sola vez por participante, en cada brazo. Es opcional: si está vacío, no se manda.",
  SLUG_DE_LA_ENCUESTA,
]

/**
 * **Las OCHO filas de la tabla, en el orden del PRD.** `R3-F4-CA-01`.
 *
 * ⛔ Se compone de las dos listas y **no se escribe a mano**: una tercera copia
 * de los siete rótulos divergiría de `MENSAJES_DEL_MOTOR` sin que nada se ponga
 * rojo. El orden de esta lista **es** el orden de la tabla, y la encuesta va
 * última porque `CA-01` la enumera octava.
 */
const MENSAJES_DE_LA_TABLA: readonly Campo[] = [...MENSAJES_DEL_MOTOR, LA_ENCUESTA]

/**
 * **Sección 4 · Los DOS de derivación.** `§4 Sección 4`.
 *
 * ⛔ **El segundo se llama «Mensaje que remite al botón» y el nombre no es
 * cosmético.** `§4 S4`: llamarlo «mensaje de derivación» *«sugiere que es la
 * respuesta DEL botón, que es R2 y está fuera de R1, y el riesgo concreto es que
 * alguien cargue ahí el enlace»*. Ese enlace no va en ningún campo de R1, y por
 * eso el texto de ayuda de abajo lo dice.
 */
// ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ Los títulos de tarjeta viejos
// («Brazo con tutores» / «Brazo sin tutores», PRD_R4.md:1978-1979) quedan conservados acá en
// comentario; el texto visible pasó a «Brazo con derivación a humano» / «Brazo sin derivación a humano».
const MENSAJES_DE_DERIVACION: readonly CampoDeDerivacion[] = [
  [
    "mensajeDeNoDerivacion",
    "Mensaje",
    "Cuando alguien pide hablar con una persona y el brazo no tiene esa capacidad. La conversación sigue.",
    "Brazo sin derivación a humano",
    false,
  ],
  [
    "mensajeQueRemiteAlBoton",
    "Mensaje",
    "Cuando alguien lo pide escribiendo en un brazo que sí deriva: el texto lo manda al botón. No lleva ningún enlace.",
    "Brazo con derivación a humano",
    true,
  ],
]

/**
 * Un campo de texto, con tres formas: **suelta** (personalidad, mensajes), y
 * **en tarjeta** (derivación).
 *
 * ⭐ La tarjeta existe porque `CA-78` y `CA-79` (PRD:586-587) no dicen «un campo
 * que dice *Llega a*»: dicen **la tarjeta «Brazo sin tutores»**, o sea que el
 * nombre del brazo es el título del bloque y la dirección va adentro. Un rótulo
 * plano por campo no tiene dónde poner ese título.
 */
function CampoDeTexto({
  campo,
  rotulo,
  cuando,
  valor,
  alCambiar,
  titulo,
  llegada,
  accion,
  abierto = false,
  soloLectura = false,
  esLaEncuesta = false,
}: {
  readonly campo: CampoDeTextoDelChatbot
  readonly rotulo: string
  readonly cuando: string
  readonly valor: string
  readonly alCambiar: (valor: string) => void
  readonly titulo?: string | undefined
  readonly llegada?: string | undefined
  readonly accion?: ReactNode
  readonly abierto?: boolean
  /**
   * ⭐ **La fila se vuelve de LECTURA mientras el panel de otro mensaje está
   * abierto.** `[18/9/2026]` Ver ⟦el-editor-del-mensaje-vive-en-el-panel⟧ abajo:
   * el editor de un mensaje **no puede existir dos veces** —el `id` es uno solo—,
   * así que con el panel abierto la tabla pasa a ser la referencia que `CA-70` y
   * `CA-71` piden (Cuándo se envía, Texto actual, Largo) y la edición ocurre en
   * el panel.
   *
   * ⛔ **El `<label>` se vuelve `<span>`**, y no es cosmético: un `<label for>`
   * que apunta a un `id` que no está en esta fila es una etiqueta rota, y un
   * lector de pantalla la anuncia como si hubiera un control donde no lo hay.
   */
  readonly soloLectura?: boolean
  /**
   * ⭐⭐ `R3-UI2` · **Si esta fila es la octava, la OPCIONAL.** `R3-F4-CA-11`.
   *
   * ⛔ Es un dato de la FILA y no una propiedad del texto: `PRD_R3.md:1741` dice
   * *««Encuesta de satisfacción» vacía muestra «—»»* — **sólo esa**. Los siete
   * primeros nunca están vacíos en un entorno configurado, y una raya para ellos
   * sería la tabla inventando un estado que el producto no permite guardar.
   */
  readonly esLaEncuesta?: boolean
}): JSX.Element {
  const id = `configuracion-chatbot-${campo}`
  /**
   * ⭐ `R3-F4-CA-11` · la celda «Texto actual» de la encuesta vacía dice «—».
   *
   * ⛔ Con `trim()` y no con `=== ""`: `PRD_R3.md:1772` dice que un texto de la
   * encuesta **con solo espacios se guarda vacío**, así que mostrarlo como si
   * tuviera contenido sería la tabla diciendo una cosa y el servidor guardando
   * otra. ⚠️ El **Largo** sigue contando lo que hay (`Contador`), no lo
   * recortado: es el largo que el operador está editando.
   */
  const laEncuestaEstaVacia = esLaEncuesta && valor.trim() === ""
  const base =
    titulo === undefined ? "configuracion-chatbot__campo" : "configuracion-chatbot__tarjeta"
  return (
    /*
      ⛔ `configuracion-chatbot-fila-` y NO `…-panel-`: el prefijo `panel` ya está
      tomado por `Panel` (`pestanas.tsx`), que es lo que el `aria-controls` de cada
      pestaña apunta. Con los dos bajo el mismo prefijo, que no se pisen hoy
      depende de que ningún campo del contrato se llame igual que una pestaña
      —una coincidencia que nada hace cumplir—, y un `id` repetido rompe el
      `aria-controls` de una pestaña en silencio.
    */
    <div
      id={`configuracion-chatbot-fila-${campo}`}
      className={abierto ? `${base} ${base}--abierto` : base}
      aria-current={abierto ? "true" : undefined}
    >
      {titulo === undefined ? null : (
        <div className="configuracion-chatbot__tarjeta-cabecera">
          <h3>{titulo}</h3>
          <span className="configuracion-chatbot__llegada">{llegada}</span>
        </div>
      )}
      {soloLectura ? (
        <span className="configuracion-chatbot__rotulo">{rotulo}</span>
      ) : (
        <label htmlFor={id}>{rotulo}</label>
      )}
      <span className="configuracion-chatbot__ayuda">{cuando}</span>
      {soloLectura ? (
        <p className="configuracion-chatbot__texto-actual">{laEncuestaEstaVacia ? "—" : valor}</p>
      ) : (
        <textarea
          id={id}
          value={valor}
          onChange={(e) => {
            alCambiar(e.target.value)
          }}
          aria-invalid={!esLaEncuesta && valor.trim() === ""}
        />
      )}
      <Contador texto={valor} />
      {accion ?? null}
    </div>
  )
}

export function PantallaConfiguracionDelChatbot(): JSX.Element {
  const borde = useBordeConfiguracion()

  /**
   * ⭐ **La barra de direcciones es el estado de la pantalla**: de ella salen la
   * pestaña activa (`CA-13`…`CA-18`) y, en Mensajes del motor, la fila abierta
   * (`CA-72`, `CA-73`, `CA-74`, `CA-75`). Un solo hook, dos derivaciones.
   */
  const hash = usarElHash()
  const activa = pestanaDelHash(hash)
  const abierto = mensajeAbiertoDelHash(hash)

  /**
   * ⭐ La fila de `MENSAJES_DEL_MOTOR` que la dirección abre, o `undefined`.
   * Ver ⟦el-editor-del-mensaje-vive-en-el-panel⟧ en la sección de Mensajes.
   *
   * ⛔ Sale de la MISMA tupla que dibuja la tabla y no de una segunda lista: el
   * rótulo y el «Cuándo se envía» del panel tienen que ser los de la fila, y dos
   * fuentes divergirían sin que nada se ponga rojo.
   */
  const mensajeDelPanel = MENSAJES_DE_LA_TABLA.find(([, , , slug]) => slug === abierto)

  /**
   * ⭐ `R1.1-F5-CA-74` (`:893`) · **si hay un panel abierto, la acción principal
   * es la del panel.** Es el caso particular de `:796` («como máximo una acción
   * principal habilitada por pantalla»).
   *
   * ⛔ Arranca de la DIRECCIÓN (`CA-45`) y lo corrige `SeccionDeGuardrails`, que
   * es la única que sabe si además se está editando una fila —camino que el PRD
   * no direcciona—. Sin esa corrección, editar dejaría dos principales
   * encendidas.
   */
  const [panelDeCategoria, setPanelDeCategoria] = useState(panelDeCategoriaDelHash(hash))

  const [textos, setTextos] = useState<TextosDelChatbot | null>(null)
  /**
   * ⭐ `R1.1-F3-CA-75` (`PRD_R1.1.md:583`) · **LO PUBLICADO, para poder volver.**
   *
   * «Cancelar» tiene que dejar la fila con el texto que estaba vigente, así que
   * hace falta guardar a dónde volver. Es un segundo estado y no un `useRef`
   * porque **se renderiza**: de la comparación entre los dos sale si hay algo sin
   * guardar, y eso decide si «Cancelar» está habilitado.
   *
   * ⛔ Se actualiza en DOS lados y los dos hacen falta: al cargar (lo vigente es
   * lo que vino del servidor) y **después de guardar con éxito** (lo vigente pasa
   * a ser lo recién publicado). Sin lo segundo, «Cancelar» apretado después de
   * guardar desharía un cambio **que ya está publicado** y dejaría la pantalla
   * mintiendo sobre lo que el chatbot está usando.
   */
  const [textosPublicados, setTextosPublicados] = useState<TextosDelChatbot | null>(null)
  /**
   * ⭐⭐ `R3-UI2` · **Los textos OPCIONALES, en un estado APARTE.** `R3-CAA1` · B15.
   *
   * ⛔ No se mezclan con `textos`: `TextosDelChatbot` no tiene la clave de la
   * encuesta y meterla ahí con un `as` dejaría a `textosVacios` recorriéndola
   * como si fuera obligatoria. Son dos objetos porque son dos contratos, y
   * viajan juntos en el mismo `PUT` (B16).
   *
   * ⚠️ Arranca en `null` como `textos`: hasta que el cable contesta no hay
   * encuesta, y **rellenarla con `""` sería inventar el estado** que el contrato
   * prohíbe confundir con «el operador la dejó vacía».
   */
  const [textosOpcionales, setTextosOpcionales] = useState<TextosOpcionalesDelChatbot | null>(null)
  const [opcionalesPublicados, setOpcionalesPublicados] =
    useState<TextosOpcionalesDelChatbot | null>(null)
  const [categorias, setCategorias] = useState<readonly CategoriaDeGuardrail[]>([])
  const [error, setError] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [guardado, setGuardado] = useState(false)

  /**
   * ⭐⭐ `[24/9/2026]` · **QUÉ MITAD DEL PERFIL SE VE: EL EDITOR O LA VISTA PREVIA.**
   *
   * `PRD_R1.1.md:667` describe esta pantalla textual: *«Personalidad ·
   * `…/personalidad` | **Editor del perfil a todo el ancho que alterna «Editar» y
   * «Vista previa»**; ayuda «Se escribe en markdown…»; contador «N caracteres»
   * (sin tope); «Guardar personalidad»…»*. Y `R1.1-F3-CA-37` (`:726`) pide
   * **TOCAR**: *«tocar «Vista previa» muestra el texto «Texto base del perfil»»*.
   *
   * ⛔ Hasta hoy las dos cosas se dibujaban **a la vez, en dos columnas**, y la
   * «Vista previa» era un `<h3>`, no un control. El comentario que lo sostenía
   * decía que esconderla pondría rojo a `aw48` — y era cierto, pero el precio era
   * un producto que **no alterna nada** donde el PRD pide que alterne, y un
   * criterio que habla de tocar algo que no se podía tocar.
   *
   * ⚠️ Se guarda en estado local y **no en la dirección**: `F3-CA-13`…`CA-18` le
   * dan dirección propia a cada PESTAÑA, y ninguna a las dos mitades de ésta.
   * Inventarle una ruta sería agregar contrato que nadie pidió.
   *
   * ⭐ Arranca en `editar`, que es lo que el boceto dibuja activo (`.seg > .on`).
   */
  const [vistaDelPerfil, setVistaDelPerfil] = useState<"editar" | "vista-previa">("editar")

  const decirElError = useCallback((causa: unknown): void => {
    setError(causa instanceof Error ? causa.message : "No se pudo hablar con el servidor.")
  }, [])

  useEffect(() => {
    let vigente = true
    borde
      .configuracion()
      .then((configuracion) => {
        if (!vigente) return
        setTextos(configuracion.textos)
        setTextosPublicados(configuracion.textos)
        // ⭐ `R3-UI2` · la encuesta llega LEÍDA del cable (`leerTextosOpcionales`,
        //    ola 0b §④). Si la respuesta no la trae, `configuracion()` ya falló y
        //    esta rama no corre: una clave ausente NO se vuelve una cadena vacía.
        setTextosOpcionales(configuracion.textosOpcionales)
        setOpcionalesPublicados(configuracion.textosOpcionales)
        setCategorias(configuracion.categorias)
        setError(null)
      })
      .catch((causa: unknown) => {
        if (vigente) decirElError(causa)
      })
    return () => {
      vigente = false
    }
  }, [borde, decirElError])

  const cambiar = (campo: keyof TextosDelChatbot, valor: string): void => {
    if (textos === null) return
    setTextos({ ...textos, [campo]: valor })
    setGuardado(false)
  }

  /**
   * ⭐ `R3-UI2` · **El editor de la encuesta escribe en el OTRO objeto.**
   *
   * ⛔ Es una segunda función y no una rama de `cambiar`: el campo de `cambiar`
   * está tipado `keyof TextosDelChatbot` y ensancharlo a la unión pondría al
   * compilador a aceptar `setTextos({ ...textos, encuestaDeSatisfaccion })`, que
   * es exactamente el estado que `R3-CAA1` separó en dos tipos para que no
   * existiera.
   */
  const cambiarLaEncuesta = (valor: string): void => {
    setTextosOpcionales({ encuestaDeSatisfaccion: valor })
    setGuardado(false)
  }

  /**
   * `§5.2` · **guardar publica**: un solo pedido y el texto queda vigente. No hay
   * un segundo paso ~~, y por eso no hay un segundo botón~~.
   *
   * ⚠️ `[17/9/2026]` · `ADR-0082`: la coletilla tachada venció con
   * `R1.1-F3-CA-75` (`PRD_R1.1.md:583`), que pide un «Cancelar». **Lo que decía
   * sigue siendo cierto de lo que hablaba** —el GUARDADO es un solo paso y un
   * solo botón—; lo que no se sigue de eso es que no haya un botón para **no**
   * guardar. Son dos cosas distintas y el comentario las juntaba.
   */
  const guardarLosTextos = (): void => {
    if (textos === null || textosOpcionales === null) return
    /*
     * ⛔⛔ `R3-UI2` · **ACÁ NO SE VUELVE A REVISAR SI SE PUEDE GUARDAR, y es una
     * decisión, no un olvido.** Antes decía
     * ~~`|| !puedeGuardarLosTextos(textos)`~~ y con la obligatoriedad POR FILA eso
     * pasó a ser un defecto: el panel de la encuesta se habilita con SU regla
     * —vacía se puede guardar—, así que una instancia nueva (los siete vacíos en
     * la base) habría entrado acá, encontrado el global en `false` y **vuelto sin
     * hacer nada ni decir nada**. Un botón habilitado que no hace nada es peor
     * que un `400`.
     *
     * ⚠️ El `acceptance` de la etapa lo escribe: *«con alguno de los siete vacío
     * EN LA BASE, guardar la encuesta devuelve `400` del servidor. La pantalla
     * muestra ese error como hoy muestra un `4xx` del guardado; no lo evita
     * inventando textos.»* Quien apaga los botones es cada `disabled`.
     */
    setGuardando(true)
    borde
      // ⭐ B16 · el `PUT` lleva SIEMPRE las dos mitades. Ver `borde-configuracion.ts`.
      .guardarTextos(textos, textosOpcionales)
      .then(() => {
        setGuardado(true)
        setError(null)
        // ⛔ Lo publicado pasa a ser esto. Ver `textosPublicados`: sin esta línea,
        // «Cancelar» después de guardar desharía un cambio ya vigente.
        setTextosPublicados(textos)
        setOpcionalesPublicados(textosOpcionales)
      })
      .catch(decirElError)
      .finally(() => {
        setGuardando(false)
      })
  }

  /**
   * ⭐ `R1.1-F3-CA-75` (`PRD_R1.1.md:583`) · **«Cancelar» no guarda: vuelve a lo
   * vigente.**
   *
   * ⛔ **No navega y no vacía**: descarta lo editado y deja en pantalla el texto
   * publicado. El criterio lo dice en esos términos —*«cambiar el texto a
   * “Descartado” y tocar “Cancelar” deja la fila con “Texto base”»*—: lo que tiene
   * que quedar es **el texto de antes**, no un campo en blanco ni otra pantalla.
   *
   * ⚠️ Vuelven **los nueve textos** y no sólo el de la pestaña abierta, porque el
   * estado es uno solo y el guardado también publica los nueve (`§5.2`). Un
   * «Cancelar» que revirtiera un solo campo dejaría los otros ocho editados y sin
   * guardar, sin nada en la pantalla que lo dijera.
   */
  const cancelarLaEdicion = (): void => {
    if (textosPublicados === null) return
    setTextos(textosPublicados)
    // ⭐ `R3-UI2` · y la encuesta también vuelve: `R3-F4-CA-08` lo pide por ella
    //    («Descartado …» + «Cancelar» ⇒ la fila vuelve a «108 / 4096»), y si no
    //    volviera sería el único texto de la pantalla que «Cancelar» no cancela.
    if (opcionalesPublicados !== null) setTextosOpcionales(opcionalesPublicados)
    setGuardado(false)
  }

  /**
   * ⭐⭐ `R3-UI2` · **ESCAPE CIERRA EL PANEL DEL MENSAJE, SIN GUARDAR.**
   * `PRD_R3.md:1769`, textual: *««Cancelar» (y Escape) cierra sin guardar»*.
   *
   * ⛔ **Hasta hoy no lo hacía ningún panel de esta pantalla, y se midió**:
   * `grep -rn "Escape" src/configuracion-del-chatbot/` daba sólo comentarios —
   * `guardrails.tsx:75` describe lo que un modal necesitaría, no lo implementa—.
   * Se construye para el panel **del mensaje**, que es el que esta etapa
   * gobierna; ⚠️ el panel de alta de CATEGORÍA (`guardrails.tsx`) **sigue sin
   * Escape** y eso queda dicho, no arreglado de contrabando: ningún criterio de
   * `R3-F4` lo nombra.
   *
   * ⭐ Hace **lo mismo que «Cancelar»**: descarta lo editado y vuelve a la lista.
   * Si hiciera sólo una de las dos, «Cancelar» y Escape dejarían la pantalla en
   * estados distintos y el PRD los nombra en la misma frase.
   *
   * ⛔ En `document` y no en el `<aside>`: el foco puede estar en el `<textarea>`
   * o en ninguna parte, y un oyente colgado del panel no lo alcanza en el segundo
   * caso. Se registra **sólo con el panel abierto** y se desarma al cerrarlo.
   */
  useEffect(() => {
    if (mensajeDelPanel === undefined) return
    const alPresionar = (evento: KeyboardEvent): void => {
      if (evento.key !== "Escape") return
      cancelarLaEdicion()
      window.location.hash = urlDeLaPestana("mensajes")
    }
    document.addEventListener("keydown", alPresionar)
    return () => {
      document.removeEventListener("keydown", alPresionar)
    }
  })

  /**
   * ¿Hay algo editado sin guardar? De acá sale si «Cancelar» tiene algo que hacer.
   *
   * ⭐ Se compara **campo por campo** y no por identidad: `cambiar` arma un objeto
   * nuevo en cada tecla, así que `textos !== textosPublicados` daría `true` apenas
   * alguien escribe y borra una letra — y «Cancelar» quedaría habilitado sin nada
   * que cancelar.
   */
  const haySinGuardar =
    (textos !== null &&
      textosPublicados !== null &&
      (Object.keys(textos) as (keyof TextosDelChatbot)[]).some(
        (campo) => textos[campo] !== textosPublicados[campo],
      )) ||
    // ⭐ `R3-UI2` · la encuesta cuenta igual que los diez: editarla y no guardarla
    //    es tener algo sin guardar, y «Cancelar» tiene qué hacer.
    (textosOpcionales !== null &&
      opcionalesPublicados !== null &&
      textosOpcionales.encuestaDeSatisfaccion !== opcionalesPublicados.encuestaDeSatisfaccion)

  /*
   * ⛔ `R3-UI2` · **`textosOpcionales === null` entra en la MISMA puerta que
   * `textos === null`, y no en una segunda.** Son las dos mitades de la misma
   * respuesta (`leerConfiguracion` devuelve las dos o ninguna): una pantalla que
   * se dibujara con los diez textos y sin la encuesta tendría que inventar un
   * valor para la octava fila, que es el `?? ""` que el contrato prohíbe.
   */
  if (textos === null || textosOpcionales === null) {
    return (
      <section
        className="configuracion-del-chatbot"
        aria-labelledby="configuracion-chatbot-titulo"
      >
        <h1 id="configuracion-chatbot-titulo">Configuración del chatbot</h1>
        {error === null ? (
          <p className="configuracion-chatbot__vacio">Cargando la configuración…</p>
        ) : (
          <p className="configuracion-chatbot__alerta" role="alert">
            {error}
          </p>
        )}
      </section>
    )
  }

  const vacios = textosVacios(textos)
  /**
   * ⭐ `R3-UI2` · **La encuesta suma al conteo de pasados y NO al de vacíos.**
   * Es la asimetría entera de `R3-F4`: `""` es un valor válido y 4.097 caracteres
   * no lo son (`CA-07` · `CA-11`). `textosVacios` no la mira porque no está en
   * `TODOS_LOS_TEXTOS` (B15) — y no tiene que estarlo.
   */
  const pasados = [
    ...textosPasadosDeTope(textos),
    ...(encuestaPasadaDeTope(textosOpcionales) ? (["encuestaDeSatisfaccion"] as const) : []),
  ]

  /**
   * ⭐ `R3-UI2` · **De dónde sale el texto de cada fila**, según a cuál de los dos
   * contratos pertenece su campo. Es la única costura entre las dos mitades, y
   * vive acá —después de la puerta de arriba— para que **no haya ningún valor de
   * repuesto**: los dos objetos ya existen.
   */
  const textoDelCampo = (campo: CampoDeTextoDelChatbot): string =>
    campo === "encuestaDeSatisfaccion" ? textosOpcionales.encuestaDeSatisfaccion : textos[campo]

  /** La mitad escritora de `textoDelCampo`: cada fila escribe en su objeto. */
  const cambiarElTextoDeLaFila = (campo: CampoDeTextoDelChatbot, valor: string): void => {
    if (campo === "encuestaDeSatisfaccion") cambiarLaEncuesta(valor)
    else cambiar(campo, valor)
  }

  /** ⭐ `R3-UI2` · si la fila que el panel tiene abierta es la octava. */
  const elPanelEsLaEncuesta = mensajeDelPanel?.[3] === SLUG_DE_LA_ENCUESTA

  /**
   * ⭐⭐ `R3-F4-CA-05` · **La ayuda del panel, y `null` en los otros siete.**
   * `PRD_R3.md:1768` la enumera textual como *«Ayuda (solo en «Encuesta de
   * satisfacción»)»*.
   *
   * ⛔ **Dibujarla siempre sería MENTIRLE al Administrador en siete paneles**:
   * les diría que puede dejar el texto vacío, y no puede (`CA-06`). Por eso
   * cuelga de `elPanelEsLaEncuesta` y no de una constante que el panel pinte sin
   * mirar qué fila abrió.
   */
  const ayudaDelPanel = elPanelEsLaEncuesta ? "Incluye el link del formulario. Si lo dejas vacío, no se manda ninguna encuesta. Cada participante recibe este mensaje una sola vez." : null

  return (
    <section className="configuracion-del-chatbot" aria-labelledby="configuracion-chatbot-titulo">
      <h1 id="configuracion-chatbot-titulo">Configuración del chatbot</h1>

      {/*
        `F3-CA-10` (PRD:518), textual. Va en la CABECERA y fuera de los paneles:
        la frase explica para qué sirve la pantalla entera, no la pestaña que está
        abierta.
      */}
      {/*
        ⭐⭐ `[21/9/2026]` **EL AVISO VUELVE A LA CABECERA, EN LÍNEA, COMO LO
        DIBUJA EL BOCETO — lo pidió el operador mirando la pantalla.**

        Acá había DOS `<p className="…__nota" role="note">` que se pintaban como
        dos cajas de aviso apiladas. `ConfigGuardrails.dc.html` dice el aviso
        dentro del mismo párrafo, con la primera mitad en negrita:
        *«Cómo habla el chatbot y qué contesta cuando no puede ayudar.
        <strong>Aplica a los dos brazos</strong> (Híbrido y Puro) y cada cambio
        queda vigente apenas se guarda.»* — una sola cabecera, sin caja.

        ⛔ **Los dos criterios siguen verdes, y por eso se pudo hacer:**
        · `R1.1-F3-CA-11` (`PRD_R1.1.md:700`) pide que **«la cabecera DICE»** el
          texto. Exige el TEXTO en la cabecera, **no una caja** — el `<strong>`
          no lo parte: el `textContent` del `<p>` lo sigue diciendo entero.
        · `R1.1-F3-CA-12` (`:701`) pide que el aviso **no tenga botón de cierre**.
          Un párrafo no tiene ninguno.

        ⚠️ **El `role="note"` se conserva** sobre el `<p>` entero: el aviso sigue
        siendo una nota para quien no ve la negrita, que es lo que `CA-12` da por
        supuesto al hablar de «el aviso de la cabecera».

        ⚠️ **Y el segundo aviso —el del costo operativo— se queda, sin caja.** No
        está en ningún boceto y ningún criterio lo pide (`grep "costo operativo"`
        sobre el PRD → 0), pero dice algo cierto y operativo. Baja a texto de
        apoyo en vez de borrarse: `sistema-visual.md §3.10` fija **un solo aviso
        por pantalla**, y el de `CA-11` es el que tiene dueño.
      */}
      {/*
        ⟦rastro · ADR-0082 · 30/9/2026 · fix/consola-literales-de-brazo⟧ Acá decía
        ~~`<strong>Aplica a los dos brazos</strong> (Híbrido y Puro)`~~ escrito a
        mano, y la consola de `OLI` (un brazo) lo mostraba igual. Sale de la
        declaración (`zonas.ts` §`losBrazos`): sobre `KAI` el `textContent` es
        idéntico al de `CA-11`; sobre `OLI` dice «Aplica al Brazo Híbrido», sin
        paréntesis, porque con un brazo no hay nada que aclarar.
      */}
      <p className="configuracion-chatbot__intro" role="note">
        Cómo habla el chatbot y qué contesta cuando no puede ayudar.{" "}
        <strong>Aplica {aLosBrazos()}</strong>
        {brazos().length >= 2 ? ` (${nombresDeLosBrazos()})` : ""} y cada cambio queda vigente
        apenas se guarda.
      </p>

      <p className="configuracion-chatbot__intro">
        Y esa vigencia inmediata tiene un costo operativo: después de cambiar el perfil de
        personalidad o los guardrails hay que correr el subconjunto sensible del conjunto de
        evaluación. Cuando se corre, el cambio ya está vigente y ya le puede haber llegado a un
        participante.
      </p>

      {error === null ? null : (
        <p className="configuracion-chatbot__alerta" role="alert">
          {error}
        </p>
      )}

      {/*
        `F3-CA-13` (PRD:521) y `F3-CA-14`…`CA-18` (PRD:522-526): cinco pestañas, en
        este orden, cada una con su dirección.
      */}
      <Pestanas activa={activa} />

      {/* ── Panel 1 · Personalidad ────────────────────────────────────────── */}
      <Panel pestana="personalidad" activa={activa}>
        <section
          className="configuracion-chatbot__seccion"
          aria-labelledby="configuracion-chatbot-personalidad"
        >
          {/*
            ⭐⭐ `[24/9/2026]` · **EL ENCABEZADO Y EL CONTROL SEGMENTADO, EN UNA
            FILA**, como el boceto: a la izquierda el título y su ayuda, a la
            derecha `.seg` con «Editar» / «Vista previa», alineados abajo.
          */}
          <div className="configuracion-chatbot__cabecera-de-seccion">
            <div>
              <h2 id="configuracion-chatbot-personalidad">Personalidad</h2>
              <p className="configuracion-chatbot__ayuda">
                El tono, el trato y las reglas de estilo. En la práctica es casi la totalidad del
                {/* `R1.1-F5-CA-85` · ~~«se guarda acá»~~ → «se guarda aquí» (`PRD_R1.1.md:904`). */}
                prompt del sistema: lo que se guarda aquí se le manda al modelo.
              </p>
            </div>

            {/*
              ⛔ **Dos `<button>` con `aria-pressed`, y no un `<h3>`.** El bloque de
              abajo decía, con razón para el producto de ayer, que *«un control que
              no cambia nada es exactamente el "control donde no lo hay" de
              `R1-F4 §5.3`»*. Ahora SÍ cambia algo, así que tiene que ser un
              control de verdad: `R1.1-F3-CA-37` pide **tocar** «Vista previa».

              ⚠️ `aria-pressed` y no `role="tab"`: las pestañas de esta pantalla ya
              son `role="tablist"` (`pestanas.tsx`) y cada una tiene dirección
              propia. Un segundo `tablist` anidado, sin dirección, le mentiría al
              lector de pantalla sobre qué se puede navegar.
            */}
            <div className="configuracion-chatbot__segmentado" role="group" aria-label="Cómo se ve el perfil">
              <button
                type="button"
                aria-pressed={vistaDelPerfil === "editar"}
                onClick={() => {
                  setVistaDelPerfil("editar")
                }}
              >
                Editar
              </button>
              <button
                type="button"
                aria-pressed={vistaDelPerfil === "vista-previa"}
                onClick={() => {
                  setVistaDelPerfil("vista-previa")
                }}
              >
                Vista previa
              </button>
            </div>
          </div>

          {/*
            ⛔ **A TODO EL ANCHO Y UNA MITAD POR VEZ**, que es lo que
            `PRD_R1.1.md:667` pide textual: *«Editor del perfil **a todo el ancho
            que alterna** "Editar" y "Vista previa"»*. Antes era
            `configuracion-chatbot__columnas columnas` — las dos a la vez, cada una
            a media hoja, para un texto que el propio PRD llama «casi todo el
            prompt del sistema».
          */}
          <div className="configuracion-chatbot__perfil-zona">
            {/*
              ⛔ El `hidden` va en el ENVOLTORIO y no en cada hijo: con el editor
              apagado, un `__campo` vacío seguía ocupando su `margin-top` y dejaba
              un hueco arriba de la vista previa. Se vio en una captura, no en un
              examen. El `id` del perfil sigue en el árbol igual — `hidden` no
              desmonta.
            */}
            <div className="configuracion-chatbot__campo" hidden={vistaDelPerfil !== "editar"}>
              <label htmlFor="configuracion-chatbot-personalityProfile">
                Perfil de personalidad
              </label>
              <textarea
                id="configuracion-chatbot-personalityProfile"
                className="configuracion-chatbot__perfil"
                value={textos.personalityProfile}
                onChange={(e) => {
                  cambiar("personalityProfile", e.target.value)
                }}
                aria-invalid={textos.personalityProfile.trim() === ""}
              />
              {/*
                ⭐⭐ ⟦el-pie-del-campo-va-antes-del-contador⟧ · `ADR-0319 ②`.
                Los DOS bocetos dibujan el pie de campo con **el texto primero y
                el contador después**, con la misma estructura:

                  · `ConfiguracionDelChatbot.dc.html` → `<div class="pie-campo">
                    <span>Se escribe en markdown · se guarda como texto plano
                    </span><span class="mono">759 caracteres</span></div>`
                  · `ConfigMensajeEditar.dc.html:146` → `<div class="pie-campo">
                    <span>Obligatorio</span><span class="mono">63 / 4096</span>
                    </div>`

                Ningún criterio nombra esa diferencia, así que manda el boceto.
                Hasta el 21/9/2026 esta pantalla lo tenía al revés —contador y
                después pie— y era la ÚNICA de las dos: el panel de mensaje ya
                quedó en el orden del boceto.

                ⛔ El barrido de `r11-orden-de-bloques` **no puede ver este
                orden**: ni `configuracion-chatbot__pie` ni
                `configuracion-chatbot__contador` están en `ANCLAS_DE_LA_APP`.
                Se mide en `pruebas/r11-pie-de-paneles.test.tsx`.
              */}
              {/*
                ⭐⭐ ⟦el-pie-de-campo-es-UNA-fila⟧ · el envoltorio que cierra el
                límite que `.configuracion-chatbot__pie` declaraba abierto en la
                hoja: *«el boceto los pone en UNA fila; el producto los apila;
                juntarlos es un cambio de JSX, no de hoja»*. Éste es ese cambio.

                El boceto dibuja `<div class="pie-campo"><span>Se escribe en
                markdown · se guarda como texto plano</span><span class="mono">759
                caracteres</span></div>` — rótulo a la izquierda, contador a la
                derecha, en una sola fila. Sin el envoltorio quedaban apilados,
                porque `__campo` es `flex-direction:column`.

                ⭐ Es la MISMA clase que el pie del panel de categoría en
                `guardrails.tsx` y que el de acá abajo: **las tres pantallas de
                la zona hablan igual**.
              */}
            </div>

            {/*
              ⭐⭐ RASTRO `[24/9/2026]` · **LA VISTA PREVIA PASÓ A SER LA OTRA MITAD
              DEL CONTROL SEGMENTADO.** Acá decía que el bloque estaba a la vista
              y no detrás de un control ~~«y eso es deliberado: el examen `aw48`
              fija la vista previa EN EL ÁRBOL apenas monta la pantalla … así que
              esconderla detrás de un botón pondría ese examen en rojo»~~.

              Era cierto sobre `aw48` y **el precio era el producto**: dos mitades
              a media hoja que no alternaban nada, donde `PRD_R1.1.md:667` pide
              *«editor del perfil **a todo el ancho que alterna** "Editar" y "Vista
              previa"»*, y un `CA-37` que habla de **tocar** algo que no se podía
              tocar.

              ⛔ **Se usa `hidden` y no se desmonta**, y no es comodidad: el
              `<textarea>` conserva su `id` en el árbol, que es de lo que cuelgan
              los casos de `aw48` que buscan los campos obligatorios. Alternar no
              tiene por qué destruir el campo.

              ⚠️ El `h3` sólo existe en la vista previa: con el editor a la vista,
              el `.seg` ya nombra las dos mitades y un encabezado suelto sería un
              título sin sección debajo.
            */}
            <div className="configuracion-chatbot__campo" hidden={vistaDelPerfil !== "vista-previa"}>
              <h3>Vista previa</h3>
              <VistaPreviaDelPerfil fuente={textos.personalityProfile} />
            </div>

            {/*
              ⭐⭐ `[24/9/2026]` · **EL PIE VA ACÁ, AL FINAL DE LA ZONA, Y NO
              ADENTRO DEL EDITOR.** Lo destapó una captura del navegador: con el
              editor escondido, el pie —que NO se esconde, porque el contador vale
              en las dos mitades— quedaba **arriba** de la vista previa, porque
              vivía adentro del bloque del editor y ese bloque va primero.

              Acá abajo queda debajo de la mitad que esté a la vista, en las dos,
              que es donde el boceto lo dibuja.

              ⛔ Sigue siendo UNA fila (⟦el-pie-de-campo-es-UNA-fila⟧) y conserva
              el orden que los dos bocetos fijan (⟦el-pie-del-campo-va-antes-del-contador⟧,
              `ADR-0319 ②`): **el texto primero y el contador después**.
            */}
            <div className="configuracion-chatbot__pie-campo">
              {/* `F3-CA-49` (PRD:557), textual. */}
              <span className="configuracion-chatbot__pie">
                Se escribe en markdown · se guarda como texto plano
              </span>
              {/*
                ⭐ El contador del perfil NO lleva el tope de la plataforma de
                mensajería, y no es un olvido: `§5.6` le da un tope propio y
                mayor, y su valor está pendiente del TL. Se cuenta y no se
                bloquea.
              */}
              <ContadorSinTope texto={textos.personalityProfile} />
            </div>
          </div>
        </section>
      </Panel>

      {/* ── Panel 2 · Guardrails ──────────────────────────────────────────── */}
      <Panel pestana="guardrails" activa={activa}>
        <SeccionDeGuardrails
          categorias={categorias}
          borde={borde}
          alCambiar={setCategorias}
          alFallar={decirElError}
          abrirNueva={panelDeCategoriaDelHash(hash)}
          alPanel={setPanelDeCategoria}
        />
      </Panel>

      {/* ── Panel 3 · Mensajes del motor ──────────────────────────────────── */}
      <Panel pestana="mensajes" activa={activa}>
        <section
          className="configuracion-chatbot__seccion"
          aria-labelledby="configuracion-chatbot-motor"
        >
          <h2 id="configuracion-chatbot-motor">Mensajes del motor</h2>
          {/*
            ⭐ `R2-UI9` · `PRD_R2.md:2505` pide *«ayuda con «Los siete son
            obligatorios»»*. Acá decía «Los seis textos… Todos obligatorios: sin
            ellos el proceso no arranca.» y **sólo cambia el numeral**: el resto de
            la frase se conserva porque dice lo que `:2505` pide —que son
            obligatorios— con más precisión que el resumen de la tabla del PRD.
          */}
          <p className="configuracion-chatbot__ayuda">
            Los siete textos que el motor le manda al participante cuando no puede contestar lo que
            le preguntaron. Todos obligatorios: sin ellos el proceso no arranca.
          </p>

          {/*
            `F3-CA-70` (PRD:578): la tabla tiene «Cuándo se envía», «Texto actual»
            y «Largo». `F3-CA-71` (PRD:579): cada fila dice su largo contra el
            tope, que es lo que el `Contador` de cada campo dibuja.

            ⛔ **Es una cabecera y no un `<table>` de verdad**, y no es por gusto:
            el examen `aw48` localiza cada campo por `#configuracion-chatbot-<campo>`
            y las categorías por `li.configuracion-chatbot__categoria strong`. Un
            `<table>` obligaría a reescribir dos exámenes que hoy están en verde, y
            los tres rótulos que el criterio pide se leen igual.
          */}
          {/*
            ⭐⭐ `[24/9/2026]` · **EL CONTENEDOR DE LA LISTA, QUE FALTABA.**

            La cabecera y las siete filas eran HERMANAS sueltas dentro de la
            `<section>`: tres rótulos en una línea y debajo siete bloques apilados
            a lo ancho de `--ancho-documento`, sin ninguna columna en común. Los
            rótulos no encabezaban nada.

            `sistema-visual.md` §3.7 —que nombra a **«Mensajes»** por su nombre—
            pide una **lista de filas** con *«Contenedor: radio 12px y borde
            `regla`»*, y el boceto la dibuja igual (`.lista{border-radius:12px;
            overflow:hidden;box-shadow:0 0 0 1px #d2d2d7}`). Sin un elemento que
            envuelva a las ocho, ese contenedor **no se puede escribir en CSS**.

            ⛔ El `<aside>` del panel lateral queda AFUERA a propósito: es una capa
            por encima de la pantalla (§3.12), no una fila de la lista.

            ⚠️ Y el nombre lleva `lista` a propósito: el clasificador de
            `qa/_ejecucion-R1-1/_sondas/estructura.ts` mapea `lista` → bloque
            `tabla` **en los dos lados**, así que esto además cierra el delta que
            la sonda de R2 media contra `ConfigMensajesBotonSinRespuesta`, donde el
            boceto declaraba `tabla` y el producto no declaraba nada.
          */}
          <div className="configuracion-chatbot__lista">
            <div className="configuracion-chatbot__columnas-cabecera">
              <span>Cuándo se envía</span>
              <span>Texto actual</span>
              <span>Largo</span>
            </div>

            {/*
              ⭐⭐ `R3-UI2` · **OCHO filas y no siete**, y salen de
              `MENSAJES_DE_LA_TABLA` —los siete obligatorios más la encuesta—.
              `PRD_R3.md:1739`: *«Ocho filas fijas, siempre en el mismo orden.
              Sin paginado, sin «Eliminar» y sin «Nuevo»»*. Acá no hay ninguno de
              los tres: la fila de la encuesta tiene el mismo «Editar» que las
              otras siete (`CA-04`: se edita, no se elimina).
            */}
            {MENSAJES_DE_LA_TABLA.map(([campo, rotulo, cuando, slug]) => (
            <CampoDeTexto
              key={campo}
              campo={campo}
              rotulo={rotulo}
              cuando={cuando}
              valor={textoDelCampo(campo)}
              esLaEncuesta={slug === SLUG_DE_LA_ENCUESTA}
              alCambiar={(valor) => {
                cambiarElTextoDeLaFila(campo, valor)
              }}
              abierto={abierto === slug}
              /*
                ⭐⭐ **RASTRO `[24/9/2026]` · LA FILA NO SE EDITA. SIEMPRE ES TEXTO.**
                Decía ~~`soloLectura={abierto !== null}`~~ — de lectura sólo mientras
                otro panel estuviera abierto.

                Lo pidió el dueño mirando la pantalla, dos veces: *«el contenido de
                la columna "Texto actual" … en los bocetos es sólo texto. Luego
                cuando se abre el sidemodal de editar, ahí sí»*.

                ── LA FUENTE ────────────────────────────────────────────────────
                · El boceto (`ConfigMensajes.dc.html`, `ConfigMensajesBotonSinRespuesta`)
                  pinta la celda como `<p class="trunca">`: dos renglones de texto,
                  sin control.
                · `PRD_R1.1.md:672` pone el editor en el panel: *«Editar mensaje ·
                  …/mensajes/<mensaje> … **Panel lateral**: Cuándo se envía, Texto
                  (obligatorio), contador, "Cancelar" y "Guardar mensaje"»*.
                · Y la fila ya tenía su «Editar» (`F3-CA-72`), que es la puerta.

                ⛔⛔ **NINGÚN CRITERIO PEDÍA QUE SE EDITARA EN LA FILA, y eso se
                verificó por identificador antes de tocar nada.** El examen que lo
                sostenía —`r11-paneles-laterales`, «los seis campos se editan en la
                fila»— dice en su comentario *«es lo que `CA-70` mide»*, y
                `R1.1-F3-CA-70` (`PRD_R1.1.md:759`) dice textual: *«Mensajes del
                motor **muestra sus columnas**: … la tabla tiene Cuándo se envía,
                Texto actual y Largo»*. **Su sujeto son las columnas.** Cita
                correcta, conclusión falsa — el modo de falla que `ADR-0319`
                documenta. Por su regla ②, manda el boceto.

                ⚠️ **Costo medido antes de decidir:** la suite pasaba de `1037·3` a
                `1028·12`. Los nueve casos se adaptaron **cambiando por dónde llegan
                al campo —del renglón al panel—, sin tocar lo que afirman**; el
                único cuya ASERCIÓN cambió es el de arriba, que afirmaba justo esto.
              */
              soloLectura
              accion={
                /*
                  `F3-CA-72` (PRD:580): «Editar» en la fila de «Fuera de tema» lleva
                  a `#/instancia/configuracion-del-chatbot/mensajes/fuera-de-tema`.

                  ⛔ Es un enlace y no un botón: la dirección tiene que sobrevivir
                  al «atrás», a copiarse y a abrirse en otra pestaña, y un `onClick`
                  con `history.push` no da ninguna de las tres.

                  ⭐ Y su rótulo es «Editar» y no el nombre del mensaje: `CA-41`
                  (PRD:549) prohíbe en estas pestañas «Eliminar» y «Borrar», no
                  «Editar», y el nombre del mensaje ya está en el `label` del campo
                  que el enlace abre.
                */
                <a
                  className="configuracion-chatbot__editar"
                  href={`${urlDeLaPestana("mensajes")}/${slug}`}
                >
                  Editar
                </a>
              }
            />
            ))}
          </div>

          {/*
            ═══════════════════════════════════════════════════════════════════
            ⛔⛔ ⟦el-editor-del-mensaje-vive-en-el-panel⟧ · `[18/9/2026]`
            ═══════════════════════════════════════════════════════════════════

            `PRD_R1.1.md:672` pide, textual: *«Editar mensaje · …/mensajes/<mensaje>
            … **Panel lateral**: Cuándo se envía, Texto (obligatorio), contador,
            “Cancelar” y “Guardar mensaje”»*.

            ⛔ **No existía NINGÚN panel.** `…/mensajes/fuera-de-tema` dibujaba
            exactamente lo mismo que `…/mensajes` —medido el 18/9 con
            `pruebas/r11-orden-de-bloques.test.tsx`: las mismas 30 anclas—; la
            dirección sólo resaltaba la fila con `--abierto`. «Guardar mensaje»
            existía únicamente como valor de `NOMBRE_DEL_GUARDADO`, y «Cuándo se
            envía» sólo como encabezado de columna.

            ⛔⛔ **Y `R1.1-F3-CA-73` estaba VERDE**: pide leer «Pregunta algo fuera
            del dominio del chatbot.» en esa dirección, y ese texto estaba… en la
            tabla. El criterio se cumplía sin que el panel existiera.

            ⭐ **El editor se MUEVE, no se duplica.** El `id` de un campo es uno
            solo (`configuracion-chatbot-<campo>`), así que mientras el panel está
            abierto las seis filas quedan de lectura (`soloLectura`) y el único
            control editable de esa pestaña es el del panel. Es lo que deja que los
            exámenes que buscan el campo por `id` sigan encontrándolo — y que haya
            una sola cosa que el operador pueda tocar.
          */}
          {mensajeDelPanel === undefined ? null : (
            <aside
              className="configuracion-chatbot__panel-lateral"
              aria-labelledby="configuracion-chatbot-titulo-del-mensaje"
            >
              <header className="configuracion-chatbot__panel-cabecera">
                {/* La zona, textual del boceto «Config. chatbot · Editar mensaje». */}
                {/* ⟦rastro · 30/9/2026⟧ ~~«· aplica a los dos brazos»~~ fijo: sale de `aLosBrazos()`. */}
                <p className="configuracion-chatbot__panel-zona">
                  Mensajes del motor · aplica {aLosBrazos()}
                </p>
                <h3 id="configuracion-chatbot-titulo-del-mensaje">{mensajeDelPanel[1]}</h3>
                <a
                  className="configuracion-chatbot__panel-cerrar"
                  aria-label="Cerrar el panel del mensaje"
                  href={urlDeLaPestana("mensajes")}
                >
                  ✕
                </a>
              </header>

              {/*
                ⭐ «Cuándo se envía» con ese rótulo exacto: `:672` lo nombra como el
                primer bloque del panel, y acá es un rótulo —no el encabezado de una
                columna, que es lo único que existía antes.
              */}
              <p className="configuracion-chatbot__rotulo">Cuándo se envía</p>
              <p className="configuracion-chatbot__ayuda">{mensajeDelPanel[2]}</p>

              {/*
                ⭐⭐ `R3-F4-CA-05` · **LA AYUDA DEL PANEL, SÓLO EN LA ENCUESTA.**
                `PRD_R3.md:1768` la enumera textual como *«Ayuda (solo en
                «Encuesta de satisfacción»)»*.

                ⛔ **Dibujarla siempre sería MENTIR en los otros siete paneles**:
                les diría al Administrador que puede dejar el texto vacío, y no
                puede (`CA-06`). Por eso cuelga del slug de la fila abierta y no
                de una constante que el panel dibuje sin mirar cuál es.
              */}
              {ayudaDelPanel === null ? null : (
                <p className="configuracion-chatbot__ayuda">{ayudaDelPanel}</p>
              )}

              <div className="configuracion-chatbot__campo">
                <label htmlFor={`configuracion-chatbot-${mensajeDelPanel[0]}`}>Texto</label>
                <textarea
                  id={`configuracion-chatbot-${mensajeDelPanel[0]}`}
                  value={textoDelCampo(mensajeDelPanel[0])}
                  onChange={(e) => {
                    cambiarElTextoDeLaFila(mensajeDelPanel[0], e.target.value)
                  }}
                  /*
                    ⚠️ `R3-UI2` · la encuesta vacía **no es inválida**: marcarla
                    con `aria-invalid` le anunciaría un error a un lector de
                    pantalla por un estado que el PRD declara válido (`:1758`).
                  */
                  aria-invalid={
                    mensajeDelPanel[3] !== SLUG_DE_LA_ENCUESTA &&
                    textoDelCampo(mensajeDelPanel[0]).trim() === ""
                  }
                />
                {/*
                  ⭐ ⟦el-Texto-del-panel-dice-que-es-obligatorio⟧ ·
                  `PRD_R1.1.md:672` enumera el panel como «Cuándo se envía,
                  **Texto (obligatorio)**, contador, «Cancelar» y «Guardar
                  mensaje»», y el boceto `ConfigMensajeEditar.dc.html:146` lo
                  dibuja como pie de campo: `<div class="pie-campo"><span>
                  Obligatorio</span><span class="mono">63 / 4096</span></div>` —
                  la palabra PRIMERO y el contador después. Lo exige
                  `R1.1-F5-CA-23`.

                  ⚠️ El contador nunca faltó; lo que faltaba era esta palabra. El
                  barrido de `r11-orden-de-bloques` **no puede ver ninguna de las
                  dos**: ni `configuracion-chatbot__pie` ni
                  `configuracion-chatbot__contador` están en `ANCLAS_DE_LA_APP`.
                  Quien mida esta reparación con ese barrido va a creer que no
                  sirvió. Se mide en `pruebas/r11-pie-de-paneles.test.tsx`.
                */}
                {/*
                  ⭐⭐ ⟦el-pie-de-campo-es-UNA-fila⟧ · el mismo envoltorio que
                  Personalidad y que el panel de categoría. Boceto
                  `ConfigMensajeEditar.dc.html:146`: `<div class="pie-campo">
                  <span>Obligatorio</span><span class="mono">63 / 4096</span>
                  </div>`.

                  ⚠️ **El contador de acá NO lleva «(sumados)»**, y está bien: el
                  literal que `R1.1-F3-CA-54` exige es el del panel de CATEGORÍA
                  (`guardrails.tsx`), donde se suman mensaje y línea de ayuda.
                  Acá hay un solo campo y no hay nada que sumar. Se verificó
                  igual, antes y después de envolver, que ese criterio siguiera
                  verde: envolver un bloque es la operación que puede partir un
                  `textContent` sin que se note.
                */}
                {/*
                  ⭐ `R3-UI2` · **y en la octava dice «Opcional».**
                  `PRD_R3.md:1766` escribe el campo del panel como *«Texto …
                  Obligatorio en los siete primeros mensajes; opcional en
                  «Encuesta de satisfacción»»*. Dejar «Obligatorio» fijo sería el
                  mismo defecto que la ayuda dibujada siempre, con menos letras.
                */}
                <div className="configuracion-chatbot__pie-campo">
                  <span className="configuracion-chatbot__pie">
                    {elPanelEsLaEncuesta ? "Opcional" : "Obligatorio"}
                  </span>
                  <Contador texto={textoDelCampo(mensajeDelPanel[0])} />
                </div>
              </div>

              {/*
                ⭐⭐ ⟦el-pie-del-panel-lleva-Cancelar-PRIMERO⟧ — el mismo orden que
                el pie del panel de categoría en `guardrails.tsx`.
                `sistema-visual.md:257` (§3.12 Panel lateral): *«El pie lleva
                «Cancelar» y el botón principal («Guardar …»)»*; `PRD_R1.1.md:672`
                dice *«… «Cancelar» y «Guardar mensaje»»*; y el boceto
                `ConfigMensajeEditar.dc.html:148` lo dibuja así. Lo exige
                `R1.1-F5-CA-23`.

                ⚠️ La barra global de `:816-880` sigue con «Guardar» primero y eso
                NO es un defecto: §3.12 habla del pie de un panel lateral, no de
                una barra de pantalla (ver el razonamiento en `:864-871`).
              */}
              <div className="configuracion-chatbot__acciones">
                {/*
                  Sale por donde entró: la dirección de la lista.

                  ⛔⛔ `R3-UI2` · **Y AHORA TAMBIÉN DESCARTA.** `R3-F4-CA-08` pide
                  las DOS cosas en una frase: *«cambiar el texto a «Descartado …»
                  y tocar «Cancelar» **vuelve a `…/mensajes`** con la fila
                  «Encuesta de satisfacción» **en «108 / 4096»**»*. Hasta hoy esto
                  sólo navegaba, así que la fila volvía mostrando lo tipeado: el
                  panel «cerraba sin guardar» y la pantalla seguía mintiendo sobre
                  lo que el chatbot está usando. `PRD_R3.md:1769` lo dice corto —
                  *««Cancelar» (y Escape) cierra sin guardar»*— y `CA-08` dice qué
                  significa.

                  ⭐ Sigue siendo un `<a href>` y no un `<button>`: la dirección
                  tiene que sobrevivir al «atrás», a copiarse y a abrirse en otra
                  pestaña. El descarte se cuelga del `onClick`; la navegación la
                  sigue haciendo el `href`.

                  ⚠️ Descarta **todo lo editado**, no sólo la fila abierta, por lo
                  mismo que el «Cancelar» de la barra (ver `cancelarLaEdicion`):
                  el estado es uno solo y el guardado publica todo.
                */}
                <a
                  className="boton boton--chico"
                  href={urlDeLaPestana("mensajes")}
                  onClick={cancelarLaEdicion}
                >
                  Cancelar
                </a>
                {/*
                  ⭐ `R1.1-F5-CA-74` · con el panel abierto **la acción principal es
                  la del panel**, y por eso ésta lleva `boton--primario` y la de la
                  pantalla lo pierde (ver `panelAbierto` arriba). `CA-74` habla de
                  CUÁL es la principal, no de dónde está en el pie.

                  ⚠️ Guarda **los nueve textos**, igual que la de la pantalla: `§5.2`
                  dice que guardar publica todo y no hay guardado parcial. El rótulo
                  dice «Guardar mensaje» porque es lo que el operador vino a hacer,
                  y el alcance real está escrito en `rutas.ts` ⟦NOMBRE_DEL_GUARDADO⟧.
                */}
                {/*
                  ⛔⛔ `R3-UI2` · **LA OBLIGATORIEDAD ES POR FILA, Y ACÁ ESTÁ EL
                  CAMBIO.** Esta línea decía
                  ~~`disabled={guardando || !puedeGuardarLosTextos(textos)}`~~ —la
                  condición GLOBAL de `reglas.ts`—, y eso significaba que **el
                  panel de un mensaje se apagaba por el vacío de OTRO**. Con la
                  octava fila deja de ser un detalle: una instancia nueva tiene
                  los siete vacíos en la base, así que la encuesta —que `CA-11`
                  dice que se puede guardar vacía— **no se habría podido guardar
                  nunca**.

                  ⭐ `puedeGuardarEsteMensaje` usa las reglas de SU fila
                  (`PRD_R3.md:1770`): los siete, no vacío y ≤ 4.096; la encuesta,
                  sólo ≤ 4.096. Es lo que hace cumplir `CA-06` y `CA-07` **sin**
                  romper `CA-11`.

                  ⚠️ Lo que el SERVIDOR acepta sigue siendo otra cosa (B16: valida
                  los siete en cada guardado). Si esta pantalla manda una encuesta
                  con alguno de los siete vacío en la base, vuelve un `400` y se
                  muestra como cualquier otro error del guardado.
                */}
                <button
                  type="button"
                  className="boton boton--primario"
                  onClick={guardarLosTextos}
                  disabled={
                    guardando ||
                    !puedeGuardarEsteMensaje(mensajeDelPanel[3], textoDelCampo(mensajeDelPanel[0]))
                  }
                >
                  Guardar mensaje
                </button>
              </div>
            </aside>
          )}
        </section>
      </Panel>

      {/* ── Panel 4 · Derivación ──────────────────────────────────────────── */}
      <Panel pestana="derivacion" activa={activa}>
        <section
          className="configuracion-chatbot__seccion"
          aria-labelledby="configuracion-chatbot-derivacion"
        >
          <h2 id="configuracion-chatbot-derivacion">Derivación</h2>
          <p className="configuracion-chatbot__ayuda">
            Los dos textos se guardan una sola vez —son de la instancia, no del brazo— y el
            producto aplica el que corresponde según lo que declare la capacidad de derivación de
            cada brazo. Por eso cada tarjeta dice a qué brazo le llega.
          </p>

          {/*
            ⛔⛔ `F1-CA-02` (PRD:216): *«en Configuración del chatbot no hay ningún
            control para activar o desactivar la derivación de un brazo»*. Acá no
            hay ninguno y no es un olvido: qué brazo deriva lo declara el despliegue,
            no la consola. Las dos tarjetas dicen a dónde llega cada texto y ninguna
            lo enciende ni lo apaga.
          */}
          <div className="configuracion-chatbot__columnas columnas">
            {MENSAJES_DE_DERIVACION.map(([campo, rotulo, cuando, tarjeta, llegaAlQueDeriva]) => (
              <CampoDeTexto
                key={campo}
                campo={campo}
                rotulo={rotulo}
                cuando={cuando}
                valor={textos[campo]}
                alCambiar={(valor) => {
                  cambiar(campo, valor)
                }}
                titulo={tarjeta}
                llegada={llegadaDeLaDerivacion(llegaAlQueDeriva)}
              />
            ))}
          </div>
        </section>
      </Panel>

      {/* ── Panel 5 · Auditoría ───────────────────────────────────────────── */}
      <Panel pestana="auditoria" activa={activa}>
        {/*
          ⭐ `R2-UI7` · **La pestaña recibe si está activa, y no es cosmético.**
          Las cinco están MONTADAS a la vez (`pestanas.tsx`): sin este dato, la
          bitácora se leería UNA vez al abrir la pantalla —antes de que el
          operador guarde nada— y `CA-F6-63` vería la página vieja. Ver
          ⟦se-lee-cuando-la-pestaña-se-vuelve-activa⟧ en `auditoria.tsx`.
        */}
        <SeccionDeAuditoria activa={activa === "auditoria"} />
      </Panel>

      {/*
        ⛔⛔ EL BOTÓN VA **FUERA DE LOS PANELES**, Y ESO ES UN REQUISITO, NO UNA
        MAQUETACIÓN. `F5-CA-74` (PRD:893): en `…/guardrails/nueva` *«el único botón
        habilitado con fondo de color DENTRO DEL PANEL es «Guardar categoría»»*.
        Como acá el guardado es uno solo y publica los nueve textos (`§5.2`), el
        botón no puede vivir adentro de ningún panel: si viviera en el de Guardrails
        habría dos acciones principales en la misma superficie.

        ⭐ **Y su nombre accesible CAMBIA con la pestaña activa** mientras su
        rótulo visible sigue siendo «Guardar». Ver `NOMBRE_DEL_GUARDADO` en
        `rutas.ts`: `CA-36`, `CA-39`, `CA-40`, `CA-74`, `CA-76`, `CA-77`, `CA-81` y
        `CA-82` (PRD:544, 547-548, 582, 584-585, 589-590) hablan de «Guardar
        personalidad», «Guardar mensaje» y «Guardar derivación», y el examen `aw48`
        busca por el TEXTO «Guardar». Con un botón por pestaña habría tres
        publicaciones distintas de la misma operación; con el rótulo cambiado, el
        examen en verde se rompe.
      */}
      <div className="configuracion-chatbot__guardar">
        {vacios.length === 0 ? null : (
          <p className="configuracion-chatbot__alerta" role="alert">
            Faltan {vacios.length} campo(s) obligatorio(s). Sin todos los textos cargados el
            proceso no arranca.
          </p>
        )}
        {pasados.length === 0 ? null : (
          <p className="configuracion-chatbot__alerta" role="alert">
            {/*
              ⭐ `R3-UI2` · el aviso dice CUÁL es el tope. Decía «se pasan del
              tope» a secas, y con la octava fila el operador puede estar mirando
              un texto que el contador de OTRA pestaña cuenta: el número tiene que
              estar en el aviso, no sólo en el contador de la fila.
            */}
            {pasados.length} texto(s) se pasan del tope de {TOPE_AL_PARTICIPANTE} caracteres: un
            mensaje que no entra es un mensaje que no llega.
          </p>
        )}
        <button
          type="button"
          /*
           * ⭐ `R1.1-F5-CA-74` · **con un panel abierto, ésta deja de ser la
           * principal.** No se esconde ni se deshabilita —el guardado de los
           * nueve textos sigue disponible—: pierde el PORTADOR del color, que es
           * lo que `:796` cuenta.
           */
          /*
           * ⭐ `R1.1-F5-CA-74` · pierde el portador del color cuando hay **un**
           * panel abierto — el de categoría o el de un mensaje. `[18/9/2026]` El
           * segundo se suma acá: antes esta línea sólo miraba `panelDeCategoria`,
           * porque el panel de mensaje no existía.
           *
           * ⭐⭐ `[21/9/2026]` **Y LO PIERDE TAMBIÉN EN GUARDRAILS con el panel
           * cerrado, por decisión del dueño.** El boceto de Guardrails no dibuja
           * ningún «Guardar» de pantalla y sí pinta «+ Nueva categoría»
           * (`<span class="btn btn-p">`). El dueño decidió mover el portador.
           *
           * ⇒ **Se mueve, no se duplica.** La lista sigue teniendo exactamente un
           * botón con fondo, así que `R1.1-F5-CA-73` se cumple igual que antes;
           * lo que cambia es CUÁL, que es lo que `CA-74` dice que se decide por
           * contexto. `guardrails.tsx` lo repliega cuando su panel se abre, así
           * que tampoco hay dos en ese estado.
           *
           * ⛔ **Guardar no se esconde ni se deshabilita**: el guardado de los
           * nueve textos sigue disponible desde las cinco pestañas. Pierde el
           * color, que es lo único que `:796` cuenta.
           *
           * ⚠️ **RASTRO DEL PRIMER INTENTO, que midió mal.** La primera versión
           * cambió sólo esta línea y puso DOS casos verdes en rojo, los dos de
           * `r11-popup-de-categoria.test.tsx` —uno de ellos el **control negativo**
           * que prueba que el replegado es reversible—. Se revirtió, se llevó la
           * decisión al dueño y **recién después** se enmendaron los casos, junto
           * con el replegado de `guardrails.tsx` que la primera versión no tenía.
           *
           * ⚠️ **Y la trampa que la causó, anotada porque es barata de repetir:**
           * se midió `CA-73` —que **no tiene instrumento**— y el que muerde es
           * `CA-74`, que sí lo tiene. **Medir el criterio que uno tiene en la
           * cabeza no es medir el que gobierna la línea.**
           */
          className={
            panelDeCategoria || abierto !== null || activa === "guardrails"
              ? "boton"
              : "boton boton--primario"
          }
          onClick={guardarLosTextos}
          /*
           * ⭐ `R3-UI2` · **el botón de PANTALLA publica los diez textos MÁS la
           * encuesta**, así que su condición es la de la configuración entera y
           * ya no la de los diez solos: decía
           * ~~`!puedeGuardarLosTextos(textos)`~~, que dejaba pasar una encuesta de
           * 4.097 caracteres y se comía un `400` que esta pantalla podía evitar.
           *
           * ⛔ Y la encuesta **no suma obligatoriedad**: `puedeGuardarLaConfiguracion`
           * le aplica el tope y nada más (B15).
           */
          disabled={guardando || !puedeGuardarLaConfiguracion(textos, textosOpcionales)}
        >
          {/*
            ⛔⛔ ⟦el-rotulo-dice-el-alcance-Y-SE-VE⟧ · `[18/9/2026]`
            ═══════════════════════════════════════════════════════════════════
            **RASTRO.** Acá decía `Guardar` fijo, con el alcance sólo en un
            `aria-label`. `PRD_R1.1.md:666`-`:671` rotula **«Guardar
            personalidad»**, **«Guardar mensaje»** y **«Guardar derivación»**, y
            los bocetos también.

            ⛔ **La justificación que había para no hacerlo era circular y está
            anotada donde estaba** (`rutas.ts` ⟦NOMBRE_DEL_GUARDADO⟧): *«se
            conserva el global porque `aw48` lo fija por igualdad exacta»*. Un
            examen no es el dueño del criterio — el PRD sí —, y «ya está
            publicado» describe el defecto, no lo autoriza.

            ⭐ El `aria-label` se va: con el alcance **visible**, un nombre
            accesible distinto del rótulo sería un segundo texto que mantener, y
            la regla de «el nombre accesible empieza por el texto visible» ya la
            cumple el rótulo solo.
          */}
          {NOMBRE_DEL_GUARDADO[activa]}
        </button>
        {/*
          ⭐ `R1.1-F3-CA-75` (`PRD_R1.1.md:583`) · **«Cancelar».**

          ⛔ Va DESPUÉS de «Guardar» en el DOM y **sin** `boton--primario`: la
          acción principal de esta superficie es guardar, y dos botones con el
          portador del color vuelven la pantalla una elección entre iguales. Es la
          misma razón por la que el de arriba lo pierde con un panel abierto.

          ⛔ Su rótulo visible es «Cancelar» **y su nombre accesible también**, a
          diferencia de «Guardar», que cambia con la pestaña. `CA-75` lo nombra
          «Cancelar» a secas y no «Cancelar mensaje», y acá NO hay el conflicto
          que obligó a `NOMBRE_DEL_GUARDADO`: no existe otro «Cancelar» en la
          pantalla con el que se pueda confundir. ⚠️ El del formulario de
          categoría de `guardrails.tsx` vive en OTRO componente y sólo existe
          mientras se edita una categoría; si algún día conviven, el que tiene que
          desambiguarse es aquél, que es el de adentro del panel.

          ⚠️ Deshabilitado cuando no hay nada editado: un «Cancelar» siempre
          apretable no le dice al operador si tiene cambios sin guardar, y este
          botón es lo único en la pantalla que puede decírselo.
        */}
        <button
          type="button"
          className="boton"
          onClick={cancelarLaEdicion}
          disabled={guardando || !haySinGuardar}
        >
          Cancelar
        </button>
        {guardado ? (
          <span className="configuracion-chatbot__ok" role="status">
            Guardado. El texto ya está vigente: {largo(textos.personalityProfile)} caracteres de
            personalidad y {categorias.length} categoría(s) de guardrail.
          </span>
        ) : null}
      </div>
    </section>
  )
}
